/**
 * Service: Card Requests
 * Wraps card request queries and the submit-card edge function.
 */
import { supabase } from "@/integrations/supabase/client";
import { makeSubmitCardIdempotencyKey } from "@/lib/idempotency";

export interface CardRequestRow {
  id: string;
  tenant_id: string;
  user_id: string;
  card_color: string;
  orientation: string;
  style: string;
  style_sub_choice: string | null;
  hobby: string | null;
  status: string;
  first_name: string;
  last_name: string;
  pan_last5: string | null;
  delivery_type: string;
  branch_id: string | null;
  postal_address: any;
  selected_avatar_image_id: string | null;
  card_mock_url: string | null;
  avatar_position: any;
  avatar_scale: number;
  created_at: string;
  submitted_at: string | null;
  reviewed_at: string | null;
  rejection_reason: string | null;
  admin_notes: string | null;
  selfie_deleted_at: string | null;
}

/* ── Reads ── */

/**
 * C1: named columns only, and NO date of birth, phone or postal address.
 * This read backs consumer self-service surfaces and generic lists; the
 * approver queue uses `listReviewQueue()` and the approver detail view uses
 * `getReviewDetail()`, which are audited.
 */
const LIST_COLUMNS =
  "id, user_id, tenant_id, owning_tenant_id, program_id, ref_number, status, " +
  "first_name, last_name, pan_last5, delivery_type, branch_id, card_color, orientation, " +
  "style, style_sub_choice, hobby, selected_avatar_image_id, card_mock_url, " +
  "avatar_position, avatar_scale, background_id, funding_amount_cents, " +
  "intended_load_amount_cents, routing_state, payment_state, rejection_reason, " +
  "rejection_subject, created_at, submitted_at, reviewed_at, selfie_deleted_at";

export async function listCardRequests(opts?: { tenantId?: string; tenantIds?: string[] }) {
  let query = supabase
    .from("card_requests")
    .select(LIST_COLUMNS)
    .order("created_at", { ascending: false });
  if (opts?.tenantIds && opts.tenantIds.length > 0) {
    query = query.in("tenant_id", opts.tenantIds);
  } else if (opts?.tenantId) {
    query = query.eq("tenant_id", opts.tenantId);
  }
  return query;
}


export async function listCardRequestsForExport(opts: {
  tenantId: string;
  dateFrom?: Date | null;
  dateTo?: Date | null;
}) {
  let query = supabase
    .from("card_requests")
    .select(
      // S6-PERSIST: the export used to project 12 columns and NONE of the
      // fulfilment fields — an issuer receiving this file could not have
      // issued a card from it (no address, no branch, no PAN digits, no
      // intended load). Everything S6 captures is now reachable here.
      "ref_number, first_name, middle_name, last_name, phone, date_of_birth, status, style, style_sub_choice, hobby, submitted_at, reviewed_at, rejection_reason, admin_notes, delivery_type, branch_id, postal_address, pan_last5, intended_load_amount_cents"
    )

    .eq("tenant_id", opts.tenantId)
    .order("submitted_at", { ascending: false });

  if (opts.dateFrom) {
    query = query.gte("submitted_at", opts.dateFrom.toISOString());
  }
  if (opts.dateTo) {
    const end = new Date(opts.dateTo);
    end.setHours(23, 59, 59, 999);
    query = query.lte("submitted_at", end.toISOString());
  }

  return query;
}

/* ── C1: approver review surface (audited, field-set narrowable) ── */

/**
 * B1: the bulk queue row carries NO identity at all — no name, DOB, phone,
 * address or PAN. It carries only what is needed to pick a row. Anything
 * about the applicant is reached through `getReviewDetail`, which writes an
 * audit row per read.
 */
export type ReviewQueueRow = {
  id: string;
  ref_number: string | null;
  status: string;
  tenant_id: string;
  owning_tenant_id: string | null;
  delivery_type: string | null;
  style: string;
  style_sub_choice: string | null;
  hobby: string | null;
  card_color: string;
  orientation: string;
  selected_avatar_image_id: string | null;
  program_id: string | null;
  program_name: string | null;
  diligence_level: string | null;
  routing_state: string | null;
  routed_destination_tenant_id: string | null;
  routed_destination_tenant_name: string | null;
  payment_state: string | null;
  created_at: string;
  submitted_at: string | null;
  reviewed_at: string | null;
  handoff_exported: boolean;
  handoff_export_count: number;
  handoff_last_exported_at: string | null;
};


export type ReviewDetail = {
  id: string;
  ref_number: string | null;
  status: string;
  tenant_id: string;
  owning_tenant_id: string | null;
  user_id: string | null;
  visible_fields: string[];
  /** Keys are OMITTED when narrowed out or not justified by the diligence level. */
  identity: Partial<Record<"first_name" | "middle_name" | "last_name" | "date_of_birth" | "phone", string>>;
  delivery: Partial<Record<"postal_address" | "delivery_type" | "branch_id", unknown>>;
  card: Record<string, unknown>;
  context: {
    program: { id: string | null; name: string | null };
    diligence_level: string;
    diligence_rank: number;
    routing: Record<string, unknown>;
    order_state: Record<string, unknown>;
    timestamps: Record<string, string | null>;
  };
  handoff: {
    exported: boolean;
    export_count: number;
    last_exported_at: string | null;
    batches: Array<Record<string, unknown>>;
  };
  selfie: { state: "available" | "deleted" | "absent" | "not_permitted"; deleted_at?: string };
  decision: {
    rejection_reason: string | null;
    rejection_subject: string | null;
    admin_notes: string | null;
    reviewed_at: string | null;
  };
};

/**
 * Approver queue list. Server-side named projection with NO date of birth,
 * phone or address — a list is a bulk surface, so it is not a bulk PII
 * disclosure. Scoped to the caller's own tenant admin/approver grants.
 */
export async function listReviewQueue(tenantIds?: string[] | null) {
  const { data, error } = await supabase.rpc("admin_review_queue_list", {
    _tenant_ids: tenantIds && tenantIds.length > 0 ? tenantIds : null,
    _limit: 1000,
    _offset: 0,
  });
  if (error) return { data: null as ReviewQueueRow[] | null, total: 0, error };
  const payload = data as { o_rows?: unknown; o_total?: number } | null;
  return {
    data: ((payload?.o_rows ?? []) as unknown) as ReviewQueueRow[],
    total: payload?.o_total ?? 0,
    error: null,
  };
}

/**
 * Approver detail read. Writes a `card_request_pii_view` audit row every call.
 * Never returns the selfie URL — that disclosure is issued and audited
 * separately by `getReviewSelfieUrl()`.
 */
export async function getReviewDetail(cardRequestId: string) {
  const { data, error } = await supabase.rpc("admin_review_detail", {
    _card_request_id: cardRequestId,
  });
  if (error) return { data: null as ReviewDetail | null, error };
  return { data: (data as unknown) as ReviewDetail, error: null };
}

/**
 * Source-selfie disclosure (INV-BIO-1). Audited DISTINCTLY, at signed-URL
 * issuance rather than page mount, so the record matches the disclosure.
 */
export async function getReviewSelfieUrl(cardRequestId: string) {
  const { data, error } = await supabase.functions.invoke("review-selfie-url", {
    body: { cardRequestId },
  });
  if (error) return { state: "absent" as const, signedUrl: null, error };
  const res = data as {
    ok: boolean;
    state?: ReviewDetail["selfie"]["state"];
    signedUrl?: string | null;
    message?: string;
  };
  if (!res?.ok) {
    return { state: "absent" as const, signedUrl: null, error: new Error(res?.message ?? "Refused") };
  }
  return { state: (res.state ?? "absent") as ReviewDetail["selfie"]["state"], signedUrl: res.signedUrl ?? null, error: null };
}

/**
 * B1: the STAFF single-record read. It deliberately omits identity columns
 * (name, PAN) — on an approver surface those may only arrive through
 * `getReviewDetail()`, which writes the `card_request_pii_view` audit row and
 * applies the config-narrowable field set. An unaudited parallel read of the
 * same PII would make the audit trail incomplete while looking complete.
 */
const STAFF_DETAIL_COLUMNS =
  "id, user_id, tenant_id, owning_tenant_id, program_id, ref_number, status, " +
  "delivery_type, branch_id, card_color, orientation, " +
  "style, style_sub_choice, hobby, selected_avatar_image_id, card_mock_url, " +
  "avatar_position, avatar_scale, background_id, funding_amount_cents, " +
  "intended_load_amount_cents, routing_state, payment_state, rejection_reason, " +
  "rejection_subject, created_at, submitted_at, reviewed_at, selfie_deleted_at";

export async function getCardRequest(id: string) {
  return supabase
    .from("card_requests")
    .select(`${STAFF_DETAIL_COLUMNS}, background:tenant_card_backgrounds(image_url, is_active)`)
    .eq("id", id)
    .single();
}

export async function getUserLatestCardRequest(userId: string, tenantId?: string) {
  const selectFields = "id, status, submitted_at, rejection_reason, reviewed_at, ref_number, first_name, last_name, card_color, orientation, style, style_sub_choice, hobby, selected_avatar_image_id, avatar_position, avatar_scale, delivery_type, postal_address, created_at, funding_amount_cents, background_id, background:tenant_card_backgrounds(image_url, is_active)";
  
  // Priority order: approved/fulfilled first, then pending/waitlisted/submitted, then anything
  const statusGroups = [
    ["approved", "fulfilled"],
    ["pending", "waitlisted", "submitted"],
  ];

  for (const statuses of statusGroups) {
    let query = supabase
      .from("card_requests")
      .select(selectFields)
      .eq("user_id", userId)
      .in("status", statuses)
      .order("created_at", { ascending: false })
      .limit(1);
    if (tenantId) query = query.eq("tenant_id", tenantId);
    const result = await query.maybeSingle();
    if (result.data) return result;
  }

  // Fallback to any card request
  let fallback = supabase
    .from("card_requests")
    .select(selectFields)
    .eq("user_id", userId)
    .order("created_at", { ascending: false })
    .limit(1);
  if (tenantId) fallback = fallback.eq("tenant_id", tenantId);
  return fallback.maybeSingle();
}

export async function getCardRequestCount() {
  return supabase.from("card_requests").select("id", { count: "exact", head: true });
}

export async function getMonthlyCardRequests(monthStart: string) {
  return supabase
    .from("card_requests")
    .select("tenant_id")
    .gte("created_at", monthStart)
    .in("status", ["submitted", "approved", "fulfilled"]);
}

/* ── Client-created applications (server decides the destination) ── */

/**
 * P3.33: clients have NO INSERT privilege on card_requests. The destination
 * columns (tenant_id / owning_tenant_id / allocated_bank_id) decide which bank
 * sees the applicant's PII and pays the referral fee, so they are set by this
 * DEFINER routine from a validated tenant — never accepted from the client.
 */
export async function createClientCardRequest(
  tenantId: string,
  status: "draft" | "pending" | "waitlisted",
  payload: Record<string, unknown>
) {
  const { data, error } = await (supabase.rpc as any)("create_client_card_request", {
    _tenant_id: tenantId,
    _status: status,
    _payload: payload,
  });
  return { id: (data as string | null) ?? null, error };
}

/** Owner-scoped link of the caller's own recent avatars to their own application. */
export async function linkMyAvatarGenerations(cardRequestId: string) {
  return (supabase.rpc as any)("link_my_avatar_generations", {
    _card_request_id: cardRequestId,
  });
}

/* ── Writes (admin review via Edge Function) ── */

export async function reviewCardRequest(payload: {
  cardRequestId: string;
  decision: "approved" | "rejected";
  rejectionReason?: string;
  /** 'source_selfie' | 'generated_avatar' | 'other' — different findings. */
  rejectionSubject?: string;
  adminNotes?: string | null;
  rejectionTemplateId?: string | null;
  approvalTemplateId?: string | null;
}) {

  return supabase.functions.invoke("admin-review-card", { body: payload });
}

// approveCardRequest / rejectCardRequest removed: unused, and they wrote review
// columns straight from the browser. Review decisions go through
// reviewCardRequest() -> admin-review-card (service role) only.


/* ── Edge Function: check attempt/credit balance ── */

export async function checkAttemptBalance(tenantId: string) {
  return supabase.functions.invoke("consume-attempt", { body: { tenantId, action: "check" } });
}

/* ── Edge Function: send notification ── */

export async function sendNotification(payload: {
  type: "new_submission" | "card_approved" | "card_rejected";
  cardRequestId: string;
  tenantId: string;
  recipientEmail?: string;
  rejectionReason?: string;
}) {
  return supabase.functions.invoke("send-notification", { body: payload });
}

/* ── Edge Function: create print pack ── */

export async function createPrintPack(cardRequestId: string) {
  return supabase.functions.invoke("create-print-pack", {
    body: { cardRequestId },
  });
}

/* ── Edge Function: submit card (critical workflow) ── */

export async function submitCard(payload: {
  /** B2B/admin submissions. Required unless channel === "consumer". */
  tenantId?: string;
  /** O5: required for channel === "consumer" — no implicit program resolution. */
  programId?: string;
  cardColor: string;
  orientation: string;
  style: string;
  styleSubChoice?: string | null;
  hobby?: string | null;
  firstName: string;
  /** Optional. LOAD-BEARING: S6 prints the middle INITIAL on the card face. */
  middleName?: string | null;
  lastName: string;
  /** Contact number for issuer fulfilment. */
  phone?: string | null;
  /**
   * `YYYY-MM-DD`. KYC/OFAC IDENTITY VERIFICATION ONLY — never used to derive
   * age. Age screening is the S3 affirmation + `age` consent record.
   */
  dateOfBirth?: string | null;
  panLast5?: string | null;

  deliveryType: string;
  branchId?: string | null;
  postalAddress?: Record<string, string> | null;
  selectedAvatarImageId?: string | null;
  avatarPosition?: { x: number; y: number };
  avatarScale?: number;
  /**
   * CAPTURED PREFERENCE ONLY — the load the customer intends their ISSUING BANK
   * to place on the card after issuance. Cardmoji neither collects nor moves this
   * money, and it MUST NEVER be added to cardFeeCents or any Stripe amount.
   * Persisted to card_requests.intended_load_amount_cents for the bank handoff.
   */
  intendedLoadAmountCents?: number | null;
  /**
   * Cards on THIS order. Selects a configured price tier only; the server
   * re-resolves the fee and cross-checks it against the PaymentIntent.
   */
  quantity?: number | null;
  /**
   * O3-F0: "consumer" makes the lead platform-owned and UNALLOCATED at insert.
   * The bank only gains visibility at payment-authorise, via allocation.
   * Omit for B2B/admin submissions, which stay bank-native.
   */
  channel?: "consumer";
  /**
   * B5: the AUTHORISED (capture_method='manual') card-fee PaymentIntent for this
   * order. The server re-verifies owner, programme, capture method and status
   * against Stripe before binding it — the client cannot assert a payment.
   */
  paymentIntentId?: string | null;
}) {
  const idempotencyKey = makeSubmitCardIdempotencyKey({
    tenantId: payload.tenantId ?? `program:${payload.programId ?? "none"}`,
    style: payload.style,
    cardColor: payload.cardColor,
    firstName: payload.firstName,
    lastName: payload.lastName,
  });

  return supabase.functions.invoke("submit-card", {
    body: { action: "submit", ...payload },
    headers: { "Idempotency-Key": idempotencyKey },
  });
}
